import React, { useState, useEffect } from "react";
import "./AboutSection.css";
import carImage from "./assets/car-interior.jpeg";
import whyskc from "./assets/whyskcjapan.jpg";

const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      const elements = document.querySelectorAll('.fade-in');
      elements.forEach(el => {
        const elementTop = el.getBoundingClientRect().top;
        const elementVisible = 150;
        if (elementTop < window.innerHeight - elementVisible) {
          el.classList.add('active');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`about-section ${isVisible ? 'visible' : ''}`}>
      <div className="about-content">
        <div className="mainbox fade-in">
          <div className="quote-icon">❝</div>
          <p className="mainsent">
            We see our customers as invited guests to a party, and we are the hosts. It's our job every day to make every important aspect of the customer experience a little bit better.
          </p>
        </div>

        <div className="row mt-5 py-5">
          <div className="col-sm-6">
            <div className="section fade-in">
              <div className="section-header">
                <div className="section-icon">🚙</div>
                <h2>Who We Are</h2>
              </div>
              <p>
                At <b>SKC Japan</b>, we specialize in sourcing and exporting high-quality used vehicles directly from Japan. Every vehicle in our catalog is hand-picked by our experienced team after undergoing strict quality inspections, ensuring that what reaches you is nothing short of the best.
              </p>
              <p>
                We never compromise on vehicle condition, appearance, or performance. Our commitment to transparency and precision guarantees a smooth, complaint-free experience for our clients—earning their trust and long-term satisfaction.
              </p>
              <p>
                From our very first step into the automotive export industry, we've continually grown through innovation, improved technologies, and streamlined systems to deliver more than just cars—we deliver peace of mind.
              </p>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="about-image fade-in">
              <div className="image-container">
                <img src={carImage} alt="Car Interior" />
                <div className="image-overlay"></div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="intro-text">
          <div className="row g-5 p-3">
            <div className="col-sm-6">
              <div className="section fade-in">
                <div className="section-header">
                  <div className="section-icon">🌍</div>
                  <h2>What We Do</h2>
                </div>
                <p>
                  We provide <b>worldwide access to high-quality Japanese cars</b>, including used cars, trucks, heavy machinery, and auto parts. Our stock is constantly updated with a wide range of reliable, affordable vehicles—all maintained in our own storage facilities to keep prices competitive.
                </p>
                <p>
                  Our buyers work directly with Japanese auctions, domestic dealers, and private owners to secure the <b>best deals on the best vehicles.</b> Once a vehicle is purchased, our fast, well-structured logistics ensure delivery in the <b>shortest time possible.</b>
                </p>
                <p>
                  With 24/7 customer service and global support teams, we're always ready—just beyond your screen—to guide you through the process, from selection to shipping.
                </p>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="section fade-in">
                <div className="section-header">
                  <div className="section-icon">🔧</div>
                  <h2>Our Services</h2>
                </div>
                <ul className="services-list">
                  <li><span className="service-icon">🚗</span> New & Used Vehicle Export</li>
                  <li><span className="service-icon">🚛</span> Heavy Machinery & Trucks</li>
                  <li><span className="service-icon">⚙️</span> Auto Parts & Accessories</li>
                  <li><span className="service-icon">📦</span> Freight Forwarding Services</li>
                  <li><span className="service-icon">🔨</span> Auction Access & Bidding Support</li>
                  <li><span className="service-icon">🎨</span> On-Demand Customization</li>
                  <li><span className="service-icon">📋</span> Vehicle Inspection & Documentation</li>
                </ul>
                <p>
                  We don't just export cars—we provide <b>end-to-end automotive solutions</b> that cover everything you need under one roof.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mission-vision-container">
          <div className="row p-5 g-5">
            <div className="col-sm-6">
              <div className="mission-card fade-in">
                <div className="card-icon">🎯</div>
                <h2>Mission</h2>
                <p>
                  To <b>empower individuals globally with the freedom of mobility</b> by delivering reliable, affordable, and top-quality vehicles from Japan.
                </p>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="vision-card fade-in">
                <div className="card-icon">👁️</div>
                <h2>Vision</h2>
                <p>
                  To become a <b>global benchmark for trust, convenience, and customer satisfaction</b> in the used vehicle export industry—living our motto:
                </p>
                <p className="vision-motto"><b>"Cars for Everyone."</b></p>
              </div>
            </div>
          </div>
        </div>

        <div className="why-choose-section">
          <div className="section-header center fade-in">
            <h2>Why Choose SKC Japan?</h2>
            <p>Discover what sets us apart from the competition</p>
          </div>
          
          <div className="row g-5 p-3">
            <div className="col-sm-6">
              <div className="benefits-content fade-in">
                <p>At SKC Japan, we combine Japanese quality standards with global expertise to deliver exceptional value to our customers worldwide.</p>
                <div className="stats-container">
                  <div className="stat">
                    <div className="stat-number">500+</div>
                    <div className="stat-label">Vehicles Exported</div>
                  </div>
                  <div className="stat">
                    <div className="stat-number">20+</div>
                    <div className="stat-label">Countries Served</div>
                  </div>
                  <div className="stat">
                    <div className="stat-number">98%</div>
                    <div className="stat-label">Customer Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="whyskc-image fade-in">
                <div className="image-container">
                  <img src={whyskc} alt="Why SKC Japan" />
                  <div className="image-overlay"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="benefits-list">
            <div className="benefit-item fade-in">
              <div className="benefit-icon">📝</div>
              <div className="benefit-content">
                <h3>Transparency</h3>
                <p>We provide complete vehicle histories in both <b>Japanese and English</b>, so you can buy with confidence—no hidden surprises.</p>
              </div>
            </div>
            
            <div className="benefit-item fade-in">
              <div className="benefit-icon">✅</div>
              <div className="benefit-content">
                <h3>Quality Assurance</h3>
                <p>Every vehicle is inspected for <b>rust, engine condition, mileage</b>, and <b>battery compatibility</b> with your local climate—so you receive a car that's ready for your roads.</p>
              </div>
            </div>
            
            <div className="benefit-item fade-in">
              <div className="benefit-icon">🔧</div>
              <div className="benefit-content">
                <h3>Parts Availability</h3>
                <p>Need spare parts after your purchase? We offer <b>genuine, affordable parts</b> on demand—supporting both end-users and resellers.</p>
              </div>
            </div>
            
            <div className="benefit-item fade-in">
              <div className="benefit-icon">➕</div>
              <div className="benefit-content">
                <h3>Customization Options</h3>
                <p>From <b>new tires and wheels</b> to <b>navigation systems, cameras, and accessories</b>, we offer add-on upgrades to match your preferences before shipment.</p>
              </div>
            </div>
            
            <div className="benefit-item fade-in">
              <div className="benefit-icon">🌱</div>
              <div className="benefit-content">
                <h3>Eco Testing</h3>
                <p>To help you avoid environmental issues post-arrival, our cars undergo <b>emission control testing</b> to meet your country's standards.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="one-stop-section fade-in">
          <div className="section-header center">
            <h2>📦 A One-Stop Automotive Export Hub</h2>
            <p className="section-paraghrap">SKC Japan isn't just a car exporter—we're your partner in mobility.</p>
          </div>
          
          <div className="services-grid">
            <div className="service-item">
              <div className="service-icon">🚗</div>
              <h4>New & Used Vehicles</h4>
            </div>
            <div className="service-item">
              <div className="service-icon">🚛</div>
              <h4>Trucks & Heavy Machinery</h4>
            </div>
            <div className="service-item">
              <div className="service-icon">🔧</div>
              <h4>Auto Parts & Tires</h4>
            </div>
            <div className="service-item">
              <div className="service-icon">🧾</div>
              <h4>Documentation & Customs</h4>
            </div>
            <div className="service-item">
              <div className="service-icon">⚓</div>
              <h4>Freight Forwarding</h4>
            </div>
            <div className="service-item">
              <div className="service-icon">🛒</div>
              <h4>Auction Access</h4>
            </div>
          </div>
        </div>

        <div className="global-section fade-in">
          <div className="global-content">
            <h2>📍 Global Presence. Local Support.</h2>
            <p>With offices and service partners worldwide, and <b>24/7 customer support</b>, SKC Japan offers not just cars—but confidence, care, and complete convenience.</p>
            <div className="globe-icon">🌐</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutSection;